class CustomSelect {
  constructor(selectElement) {
    this.selectElement = selectElement;
    this.customSelectContainer = null;
    this.customSelectTrigger = null;
    this.customSelectOptions = null;
    this.isOpen = false;
    this.selectedIndex = selectElement.selectedIndex;
    
    this.init();
  }

  init() {
    // Remove any existing custom select for this element
    const existingCustomSelect = this.selectElement.parentNode.querySelector('.custom-select');
    if (existingCustomSelect) {
      existingCustomSelect.remove();
    }
    
    // Create custom select structure
    this.createCustomSelect();
    // Bind events
    this.bindEvents();
    // Hide native select
    this.selectElement.style.display = 'none';
    this.selectElement.setAttribute('aria-hidden', 'true');
  }

  createCustomSelect() {
    // Get selected option
    const selectedOption = this.selectElement.options[this.selectElement.selectedIndex];
    
    // Create custom select container
    this.customSelectContainer = document.createElement('div');
    this.customSelectContainer.className = 'custom-select';
    this.customSelectContainer.setAttribute('role', 'listbox');
    this.customSelectContainer.setAttribute('tabindex', '0');
    this.customSelectContainer.setAttribute('aria-label', selectedOption.textContent);
    
    // Create trigger (visible part)
    this.customSelectTrigger = document.createElement('div');
    this.customSelectTrigger.className = 'custom-select__trigger';
    this.customSelectTrigger.innerHTML = `
      <span class="custom-select__value">${selectedOption.textContent}</span>
      <svg class="custom-select__icon" width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M2 4L6 8L10 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    `;
    
    // Create options container
    this.customSelectOptions = document.createElement('div');
    this.customSelectOptions.className = 'custom-select__options';
    this.customSelectOptions.setAttribute('role', 'list');
    
    // Create options
    Array.from(this.selectElement.options).forEach((option, index) => {
      const customOption = document.createElement('div');
      customOption.className = 'custom-select__option';
      customOption.textContent = option.textContent;
      customOption.dataset.value = option.value;
      customOption.dataset.index = index;
      customOption.setAttribute('role', 'option');
      customOption.setAttribute('aria-selected', index === this.selectedIndex ? 'true' : 'false');
      
      if (index === this.selectedIndex) {
        customOption.classList.add('selected');
      }
      
      this.customSelectOptions.appendChild(customOption);
    });
    
    // Append elements
    this.customSelectContainer.appendChild(this.customSelectTrigger);
    this.customSelectContainer.appendChild(this.customSelectOptions);
    
    // Insert custom select after native select
    this.selectElement.parentNode.insertBefore(this.customSelectContainer, this.selectElement.nextSibling);
  }

  bindEvents() {
    // Toggle dropdown on trigger click
    this.customSelectTrigger.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.toggle();
    });
    
    // Option click
    this.customSelectOptions.addEventListener('click', (e) => {
      const option = e.target.closest('.custom-select__option');
      if (option) {
        e.preventDefault();
        e.stopPropagation();
        this.selectOption(parseInt(option.dataset.index));
      }
    });
    
    // Keyboard navigation
    this.customSelectContainer.addEventListener('keydown', (e) => {
      this.handleKeyboard(e);
    });
    
    // Close on click outside
    document.addEventListener('click', (e) => {
      if (!this.customSelectContainer.contains(e.target)) {
        this.close();
      }
    });
    
    // Close on ESC
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
      }
    });
  }

  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    // Close all other custom selects
    document.querySelectorAll('.custom-select.open').forEach(select => {
      if (select !== this.customSelectContainer) {
        select.classList.remove('open');
      }
    });
    
    this.isOpen = true;
    this.customSelectContainer.classList.add('open');
    this.customSelectContainer.setAttribute('aria-expanded', 'true');
    
    // Scroll selected option into view
    const selectedOption = this.customSelectOptions.querySelector('.custom-select__option.selected');
    if (selectedOption) {
      selectedOption.scrollIntoView({ block: 'nearest' });
    }
  }

  close() {
    this.isOpen = false;
    this.customSelectContainer.classList.remove('open');
    this.customSelectContainer.setAttribute('aria-expanded', 'false');
  }

  selectOption(index) {
    // Update selected index
    this.selectedIndex = index;
    
    // Update native select
    this.selectElement.selectedIndex = index;
    
    // Trigger change event on native select
    const changeEvent = new Event('change', { bubbles: true });
    this.selectElement.dispatchEvent(changeEvent);
    
    // Trigger input event for Shopify facets form
    const inputEvent = new Event('input', { bubbles: true });
    this.selectElement.dispatchEvent(inputEvent);
    
    // Update custom select UI
    const selectedOption = this.selectElement.options[index];
    this.customSelectTrigger.querySelector('.custom-select__value').textContent = selectedOption.textContent;
    
    // Update options selected state
    this.customSelectOptions.querySelectorAll('.custom-select__option').forEach((option, i) => {
      if (i === index) {
        option.classList.add('selected');
        option.setAttribute('aria-selected', 'true');
      } else {
        option.classList.remove('selected');
        option.setAttribute('aria-selected', 'false');
      }
    });
    
    // Update aria-label
    this.customSelectContainer.setAttribute('aria-label', selectedOption.textContent);
    
    // Close dropdown
    this.close();
  }

  handleKeyboard(e) {
    if (!this.isOpen && (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      e.preventDefault();
      this.open();
      return;
    }
    
    if (this.isOpen) {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          this.navigateOptions(1);
          break;
        case 'ArrowUp':
          e.preventDefault();
          this.navigateOptions(-1);
          break;
        case 'Enter':
        case ' ':
          e.preventDefault();
          this.selectOption(this.selectedIndex);
          break;
        case 'Home':
          e.preventDefault();
          this.selectedIndex = 0;
          this.highlightOption();
          break;
        case 'End':
          e.preventDefault();
          this.selectedIndex = this.selectElement.options.length - 1;
          this.highlightOption();
          break;
      }
    }
  }

  navigateOptions(direction) {
    const optionsCount = this.selectElement.options.length;
    this.selectedIndex = (this.selectedIndex + direction + optionsCount) % optionsCount;
    this.highlightOption();
  }

  highlightOption() {
    const options = this.customSelectOptions.querySelectorAll('.custom-select__option');
    options.forEach((option, index) => {
      if (index === this.selectedIndex) {
        option.classList.add('highlighted');
        option.scrollIntoView({ block: 'nearest' });
      } else {
        option.classList.remove('highlighted');
      }
    });
  }
}

// Initialize all custom selects
function initCustomSelects() {
  const sortBySelect = document.getElementById('SortBy');
  if (sortBySelect) {
    // Check if already initialized by looking for sibling custom select
    const existingCustomSelect = sortBySelect.parentNode.querySelector('.custom-select');
    if (!existingCustomSelect) {
      new CustomSelect(sortBySelect);
    }
  }
}

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', initCustomSelects);

// Re-initialize after facets update (Shopify AJAX)
if (typeof window !== 'undefined') {
  window.initCustomSelects = initCustomSelects;
}
